/**
 * 标签提取函数（F-M13 从 tagParser.ts 拆分）
 *
 * 提供从流式文本中提取各类标签内容的函数：
 *   - extractAllContentFromText  一次性提取思考/工具调用/agent调用/协作结果/协作状态
 *   - extractProcessLogsFromText 仅提取 processLogs（基于 extractAllContentFromText）
 *   - parseCollabStatusTag       解析单个 [COLLAB_STATUS] 标签内容
 *   - stripThinkingAndToolCallTags 清除所有已知标签，返回纯文本
 *
 * 依赖：
 *   - tagRegex.ts 提供正则常量
 *   - processLogFactory.ts 提供 createProcessLog
 *   - agent 元信息（formatAgentIdToName）由 tagParser.ts 注入，避免循环依赖
 */
import { ProcessLogEntry } from '@/components/agent/types';
import type { CollabStatusEntry } from '@/types/agent';
import { useAgentStore } from '@/stores/agentStore';

import {
  THINK_TAG_RE,
  TOOL_CALL_TAG_RE,
  CALL_AGENT_TAG_RE,
  CALL_AGENT_ASYNC_TAG_RE,
  COLLABORATION_RESULT_TAG_RE,
  COLLAB_STATUS_TAG_RE,
} from './tagRegex';
import { createProcessLog } from './processLogFactory';

// ---------------------------------------------------------------------------
// agent 元信息（保持与 tagParser.ts 内同名函数一致；为避免循环依赖在此局部声明）
// ---------------------------------------------------------------------------

const DEFAULT_AGENT_COLOR = '#6366f1';
const DEFAULT_AGENT_AVATAR = '🤖';

const _getAgentProfile = (agentId: string) => {
  return useAgentStore.getState().agents.find(a => a.id === agentId);
};

const getAgentName = (agentId: string): string => {
  const profile = _getAgentProfile(agentId);
  if (profile) return profile.name;
  return agentId.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// ---------------------------------------------------------------------------
// 类型与常量
// ---------------------------------------------------------------------------

export interface AgentCallInfo {
  agentId: string;
  task: string;
  context?: string;
}

export interface CollaborationResultItem {
  callId: string;
  content: string;
  resultType?: string;
  taskId?: string;
}

export interface ExtractedContent {
  processLogs: ProcessLogEntry[];
  thinking: string;
  collabStatuses: CollabStatusEntry[];
  agentCalls: AgentCallInfo[];
  collaborationResults: CollaborationResultItem[];
  cleanText: string;
}

// 复用 tagParser.ts 中的 STATUS_ICONS，避免重复维护
// 此处通过动态 import 模式取值不可行（顶层 const），改为直接内联（与 tagParser 一致）
// 🔵 F-M13: 实际由 tagParser.ts 注入 STATUS_ICONS（见文件末尾 setStatusIcons）
let STATUS_ICONS_LOCAL: Record<string, string> = {
  calling: '📡',
  accepted: '🤝',
  executing: '⚡',
  completed: '✅',
  handoff: '🔄',
  returning: '🔄',
  error: '❌',
  searching: '🔍',
  running: '🟡',
  failed: '❌',
  rejected: '❌',
  timeout: '❌',
  pending: '⏱️',
  cancelled: '⏱️',
  clarifying: '💬',
};

/**
 * F-M13: 由 tagParser.ts 在加载时注入 STATUS_ICONS，保证两处一致
 * （tagParser.ts 仍导出 STATUS_ICONS 作为单一真源，本模块只是镜像）
 */
export function _setStatusIcons(icons: Record<string, string>) {
  STATUS_ICONS_LOCAL = icons;
}

const PHASE_ICONS = () => STATUS_ICONS_LOCAL;

// ---------------------------------------------------------------------------
// 提取函数
// ---------------------------------------------------------------------------

/** 解析单个 [COLLAB_STATUS:phase]inner[/COLLAB_STATUS] 标签内容 */
export const parseCollabStatusTag = (tagContent: string) => {
  const phaseMatch = tagContent.match(/\[COLLAB_STATUS:(\w+)\]([\s\S]*?)\[\/COLLAB_STATUS\]/);
  if (!phaseMatch) return null;

  const phase = phaseMatch[1];
  const inner = phaseMatch[2].trim();
  const parts = inner.split('|').map(s => s.trim()).filter(Boolean);

  return {
    phase,
    icon: parts[0] || PHASE_ICONS()[phase] || '📋',
    label: parts[1] || phase,
    detail: parts.slice(2).join(' | '),
  };
};

/** 仅提取 processLogs（基于 extractAllContentFromText） */
export const extractProcessLogsFromText = (text: string): ProcessLogEntry[] => {
  const extracted = extractAllContentFromText(text);
  return extracted.processLogs;
};

/**
 * 从文本中一次性提取所有已知标签内容：
 *   - THINK / TOOL_CALL → processLogs
 *   - CALL_AGENT / CALL_AGENT_ASYNC → agentCalls + processLogs
 *   - COLLABORATION_RESULT → collaborationResults + processLogs
 *   - COLLAB_STATUS → collabStatuses + agentCalls (calling 阶段)
 * 返回提取后的 cleanText（已移除所有标签）。
 */
export const extractAllContentFromText = (text: string): ExtractedContent => {
  const processLogs: ProcessLogEntry[] = [];
  let thinking = '';
  const collabStatuses: CollabStatusEntry[] = [];
  const agentCalls: AgentCallInfo[] = [];
  const collaborationResults: CollaborationResultItem[] = [];
  let cleanText = text;

  let match: RegExpExecArray | null;

  THINK_TAG_RE.lastIndex = 0;
  while ((match = THINK_TAG_RE.exec(text)) !== null) {
    const content = (match[1] || match[2] || '').trim();
    if (content) {
      thinking = content;
      processLogs.push(createProcessLog('thinking', '🧠 思考完成', content, 'completed'));
    }
  }
  cleanText = cleanText.replace(THINK_TAG_RE, '');

  TOOL_CALL_TAG_RE.lastIndex = 0;
  while ((match = TOOL_CALL_TAG_RE.exec(text)) !== null) {
    const content = (match[1] || match[2] || '').trim();
    if (content) {
      const firstLine = content.split('\n')[0].slice(0, 80);
      processLogs.push(createProcessLog('action', `⚙️ ${firstLine}`, content, 'completed'));
    }
  }
  cleanText = cleanText.replace(TOOL_CALL_TAG_RE, '');

  CALL_AGENT_TAG_RE.lastIndex = 0;
  while ((match = CALL_AGENT_TAG_RE.exec(text)) !== null) {
    const agentId = match[1];
    const content = match[2].trim();
    const lines = content.split('\n').filter(l => l.trim());
    let task = '';
    let context = '';
    for (const line of lines) {
      const taskMatch = line.match(/^任务[：:]\s*(.*)/);
      const ctxMatch = line.match(/^上下文[：:]\s*(.*)/);
      if (taskMatch) task = taskMatch[1].trim();
      else if (ctxMatch) context = ctxMatch[1].trim();
      else if (!task) task = line.trim();
      else context += (context ? '\n' : '') + line.trim();
    }
    agentCalls.push({ agentId, task, context: context || undefined });
    const agentLabel = getAgentName(agentId);
    processLogs.push(createProcessLog('action', `📡 调用${agentLabel}`, content, 'completed'));
  }
  cleanText = cleanText.replace(CALL_AGENT_TAG_RE, (fullMatch, p1, p2) => p2.trim());

  CALL_AGENT_ASYNC_TAG_RE.lastIndex = 0;
  while ((match = CALL_AGENT_ASYNC_TAG_RE.exec(text)) !== null) {
    const agentId = match[1];
    const content = match[2].trim();
    const lines = content.split('\n').filter(l => l.trim());
    let task = '';
    let context = '';
    for (const line of lines) {
      const taskMatch = line.match(/^任务[：:]\s*(.*)/);
      const ctxMatch = line.match(/^上下文[：:]\s*(.*)/);
      if (taskMatch) task = taskMatch[1].trim();
      else if (ctxMatch) context = ctxMatch[1].trim();
      else if (!task) task = line.trim();
      else context += (context ? '\n' : '') + line.trim();
    }
    const existingCall = agentCalls.find(ac => ac.agentId === agentId && ac.task === task);
    if (!existingCall) {
      agentCalls.push({ agentId, task, context: context || undefined });
    }
    const agentLabel = getAgentName(agentId);
    processLogs.push(createProcessLog('action', `📡 异步调用${agentLabel}`, content, 'completed'));
  }
  cleanText = cleanText.replace(CALL_AGENT_ASYNC_TAG_RE, (fullMatch, p1, p2) => p2.trim());

  COLLABORATION_RESULT_TAG_RE.lastIndex = 0;
  while ((match = COLLABORATION_RESULT_TAG_RE.exec(text)) !== null) {
    const callId = match[1] || '';
    let content = match[2].trim();
    let resultType: string | undefined;
    let taskId: string | undefined;

    // 🔴 §9-F2（2026-08-28）：闭合标签兼容带斜杠变体——后端 _push_collab_result
    // 实际以 [/RESULT_TYPE:xxx] 闭合，旧正则 \[RESULT_TYPE:\1\] 永不匹配，
    // content 残留标签 → JSON.parse 失败 → 批量任务挂载被跳过。
    const resultTypeMatch = content.match(/\[RESULT_TYPE:([^\]]+)\]\n?([\s\S]*?)\n?\[\/?RESULT_TYPE:\1\]/);
    if (resultTypeMatch) {
      resultType = resultTypeMatch[1];
      content = resultTypeMatch[2].trim();
    }

    if (content) {
      try {
        const parsed = JSON.parse(content);
        if (parsed.taskId) taskId = parsed.taskId;
        if (parsed.taskType && !resultType) resultType = parsed.taskType;
      } catch {}

      const firstLine = content.split('\n')[0].slice(0, 80);
      const typeLabel = resultType ? ` (${resultType})` : '';
      processLogs.push(createProcessLog('result', `📋 协作结果${typeLabel}`, content, 'completed'));
      collaborationResults.push({ callId, content, resultType, taskId });
    }
  }
  cleanText = cleanText.replace(COLLABORATION_RESULT_TAG_RE, '');

  COLLAB_STATUS_TAG_RE.lastIndex = 0;
  while ((match = COLLAB_STATUS_TAG_RE.exec(text)) !== null) {
    const phase = match[1];
    const inner = (match[2] || '').trim();
    const parts = inner.split('|').map(s => s.trim()).filter(Boolean);
    collabStatuses.push({
      phase,
      icon: PHASE_ICONS()[phase] || '📋',
      label: parts[0] || phase,
      detail: parts.slice(1).join(' | '),
    });
    if (phase === 'calling' && parts.length >= 3) {
      const targetAgentPart = parts[2] || '';
      const agentIdMatch = targetAgentPart.match(/(?:🔍|🎨|🖌️|🤖|🧭|📐|✍️|🎞️|🎬)\s*(.+)/);
      const targetAgentName = agentIdMatch ? agentIdMatch[1].trim() : targetAgentPart;
      const agents = useAgentStore.getState().agents;
      const matched = agents.find(a => a.name === targetAgentName);
      const resolvedAgentId = matched ? matched.id : targetAgentName;
      const taskDesc = parts.slice(3).join(' | ');
      const existingCall = agentCalls.find(ac => ac.agentId === resolvedAgentId);
      if (!existingCall && taskDesc) {
        agentCalls.push({ agentId: resolvedAgentId, task: taskDesc });
      }
    }
  }
  cleanText = cleanText.replace(COLLAB_STATUS_TAG_RE, '');

  cleanText = cleanText.replace(/\[\/?GENERATED_PROMPT\]/g, '');

  return { processLogs, thinking, collabStatuses, agentCalls, collaborationResults, cleanText };
};

/**
 * 清除文本中所有已知标签，返回纯文本。
 * preserveWhitespace=true 时保留首尾空白。
 */
export const stripThinkingAndToolCallTags = (text: string, preserveWhitespace = false): string => {
  const result = text
    // 1. 先清除完整的标签对（开闭标签在同一文本中）
    .replace(/<think[^>]*>[\s\S]*?<\/think>/g, '')
    .replace(/\[THINK\][\s\S]*?\[\/THINK\]/g, '')
    .replace(/\[TOOL_CALL\][\s\S]*?\[\/TOOL_CALL\]/g, '')
    .replace(/<tool_call[^>]*>[\s\S]*?<\/tool_call>/g, '')
    .replace(/\[CALL_AGENT:[^\]]*\]([\s\S]*?)\[\/CALL_AGENT(?::[^\]]*)?\]/g, '$1')
    .replace(/\[CALL_AGENT_ASYNC:[^\]]*\]([\s\S]*?)\[\/CALL_AGENT_ASYNC(?::[^\]]*)?\]/g, '$1')
    .replace(/\[ROUTE\][\s\S]*?\[\/ROUTE\]/g, '')
    .replace(/\[REMINDER\][\s\S]*?\[\/REMINDER\]/g, '')
    .replace(/\[COLLAB_CTX:[^\]]*\][\s\S]*?\[\/COLLAB_CTX(?::[^\]]*)?\]/g, '')
    .replace(/\[COLLABORATION_RESULT(?::[^\]]*)?\][\s\S]*?\[\/COLLABORATION_RESULT(?::[^\]]*)?\]/g, '')
    .replace(/\[RESULT_TYPE:[^\]]*\][\s\S]*?\[\/RESULT_TYPE(?::[^\]]*)?\]/g, '')
    .replace(/\[RESULT_TYPE:[^\]]+\]\n?/g, '')
    .replace(/\[COLLAB_STATUS:[^\]]*\][\s\S]*?\[\/COLLAB_STATUS\]/g, '')
    .replace(/\[\/?GENERATED_PROMPT\]/g, '')
    // 2. 清除不完整的 HTML 标签（流式场景中标签跨 chunk，只有开头没有闭合）
    // P0(v10): 只清除开标签本身，不删除后续内容
    // 原正则 /<think[^>]*>[\s\S]*$/g 中 [\s\S]*$ 会删除标签后所有内容，导致摘要截断
    // 完整标签对已在第1步清除，此处只需清除残留的开标签
    .replace(/<think[^>]*>/g, '')
    .replace(/<tool_call[^>]*>/g, '')
    // 孤立的闭合标签（前文已被其他 chunk 处理）
    .replace(/<\/think>/g, '')
    .replace(/<\/tool_call>/g, '')
    // 3. 清除不完整的方括号标签碎片（流式场景中标签可能跨 chunk，只有开头没有闭合）
    .replace(/\[\/?(?:THINK|TOOL_CALL|COLLAB_STATUS(?::[^\]]*)?|ROUTE|REMINDER|COLLAB_CTX(?::[^\]]*)?|COLLABORATION_RESULT(?::[^\]]*)?|RESULT_TYPE(?::[^\]]*)?|CALL_AGENT(?::[^\]]*)?|CALL_AGENT_ASYNC(?::[^\]]*)?|GENERATED_PROMPT)\]/g, '')
    // 4. 清理 LLM 输出的 HTML <br> 标签（表格单元格内常见），转为换行符
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\n{3,}/g, '\n\n');
  return preserveWhitespace ? result : result.trim();
};

// 显式标记 DEFAULT_AGENT_COLOR / DEFAULT_AGENT_AVATAR 已使用（避免 TS 未使用告警）
void DEFAULT_AGENT_COLOR;
void DEFAULT_AGENT_AVATAR;
